import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../../core/api/api';

export const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [count, setCount] = useState(0);
  const [filter, setFilter] = useState('todas');
  const [loading, setLoading] = useState(false);
  const prevTokenRef = useRef(localStorage.getItem('token'));
  const audioRef = useRef(null);
  const prevCountRef = useRef(0);
  const primeraCargaRef = useRef(true);

  const reproducirSonido = useCallback(() => {
    if (audioRef.current === null) {
      audioRef.current = new Audio('/sounds/Notificacions_Neuro.wav');
    }
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(() => {});
  }, []);

  const fetchNotifications = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setNotifications([]);
      setCount(0);
      return;
    }
    setLoading(true);
    try {
      const response = await api.get('/Notifications/Notification');
      const nuevoCount = response.data.count || 0;
      if (!primeraCargaRef.current && nuevoCount > prevCountRef.current) {
        reproducirSonido();
      }
      prevCountRef.current = nuevoCount;
      primeraCargaRef.current = false;
      setCount(nuevoCount);
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Error al cargar notificaciones:', error);
      setCount(0);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [reproducirSonido]);

  const markAsRead = useCallback(async (idNotificacion) => {
    try {
      await api.put('/Notifications/UpdateAsync', { idNotificacion, lectura: true });
      setNotifications(prev => prev.map(n =>
        n.idNotificacion === idNotificacion ? { ...n, lectura: true } : n
      ));
      setCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error al marcar notificación como leída:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const unread = notifications.filter(n => !n.lectura && n.idNotificacion);
    for (const n of unread) {
      await api.put('/Notifications/UpdateAsync', { idNotificacion: n.idNotificacion, lectura: true });
    }
    setNotifications(prev => prev.map(n => ({ ...n, lectura: true })));
    setCount(0);
  }, [notifications]);

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'no-leidas') return !n.lectura;
    if (filter === 'leidas') return n.lectura;
    return true;
  });

  useEffect(() => {
    setNotifications([]);
    setCount(0);
    fetchNotifications();
    const interval = setInterval(() => {
      const currentToken = localStorage.getItem('token');
      if (currentToken !== prevTokenRef.current) {
        prevTokenRef.current = currentToken;
        setNotifications([]);
        setCount(0);
        prevCountRef.current = 0;
        primeraCargaRef.current = true;
      }
      if (currentToken) {
        fetchNotifications();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  return { notifications: filteredNotifications, allNotifications: notifications, count, loading, filter, setFilter, markAsRead, markAllAsRead, refetch: fetchNotifications };
};
